import { Navigate, Link } from 'react-router-dom';
import { ReactNode } from 'react';
import { useAuth } from '../../lib/auth';
import { useCustomer } from '../../lib/customer';
import { theme } from '../../styles/theme';

/**
 * Guard para /portal — requiere session + customer_user activo.
 * Si el user es staff (NULED interno), va a /admin en su lugar.
 * Si no es customer, muestra mensaje + CTA a "Vuélvete distribuidor".
 */
export function RequirePortal({ children }: { children: ReactNode }) {
  const { session, loading: authLoading, staff } = useAuth();
  const { customer, loading: custLoading, error } = useCustomer();

  if (authLoading || custLoading) {
    return (
      <div style={loadingStyle}>Cargando…</div>
    );
  }

  if (!session) {
    return <Navigate to="/login?redirect=/portal" replace />;
  }

  // Si es staff de NULED interno, mejor mandarlo al admin
  if (staff?.active && !customer) {
    return <Navigate to="/admin" replace />;
  }

  if (error) {
    return (
      <div style={errorStyle}>
        <div style={{ fontSize: 18, color: theme.brand, fontWeight: 600 }}>
          Error cargando tu cuenta.
        </div>
        <div style={{ color: theme.textMuted, fontSize: 13, marginTop: 8 }}>{error}</div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div style={errorStyle}>
        <div style={{ fontSize: 32, marginBottom: 16 }}>👋</div>
        <div style={{ fontFamily: theme.font.head, fontSize: 22, fontWeight: 500, color: theme.brand }}>
          Aún no eres distribuidor NULED
        </div>
        <div style={{ color: theme.textMuted, fontSize: 14, marginTop: 8, lineHeight: 1.6, maxWidth: 480 }}>
          Tu cuenta existe pero no está ligada a una cuenta de distribuidor activa.
          Aplica al programa para recibir tier mayorista, capacitación Lutron y
          acceso al portal.
        </div>
        <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
          <Link to="/distribuidor" style={{
            background: theme.accent,
            color: theme.brand,
            padding: '12px 24px',
            fontSize: 13,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.4,
            textDecoration: 'none',
          }}>
            Vuélvete distribuidor
          </Link>
          <Link to="/" style={{
            border: `1px solid ${theme.border}`,
            color: theme.brand,
            padding: '12px 24px',
            fontSize: 13,
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: 0.4,
            textDecoration: 'none',
          }}>
            Volver al inicio
          </Link>
        </div>
      </div>
    );
  }

  if (customer.status !== 'active') {
    return (
      <div style={errorStyle}>
        <div style={{ fontFamily: theme.font.head, fontSize: 22, fontWeight: 500, color: theme.brand }}>
          Tu cuenta está como <strong>{customer.status}</strong>
        </div>
        <div style={{ color: theme.textMuted, fontSize: 14, marginTop: 8, maxWidth: 480 }}>
          Contacta a tu agente de ventas NULED para reactivar tu acceso al portal.
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

const loadingStyle: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: theme.font.sans,
  color: theme.textMuted,
};

const errorStyle: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  fontFamily: theme.font.sans,
  textAlign: 'center',
  padding: 24,
};
